import { useState, useEffect } from 'react';

interface AntiSpamConfig {
  minDelay: number; // Minimum delay between actions (in ms)
  burstLimit: number; // Maximum number of actions allowed in a burst
  burstWindow: number; // Time window for burst actions (in ms)
  cooldownTime: number; // Cooldown time after exceeding limits (in ms)
}

export const useAntiSpam = (config: AntiSpamConfig) => {
  const [isBlocked, setIsBlocked] = useState(false);
  const [actionTimestamps, setActionTimestamps] = useState<number[]>([]);
  const [lastActionTime, setLastActionTime] = useState<number>(0);

  const checkAction = () => {
    const now = Date.now();
    // Enforce minimum delay between actions
    if (now - lastActionTime < config.minDelay) {
      return false;
    }

    const updatedTimestamps = actionTimestamps.filter(
      (timestamp) => now - timestamp <= config.burstWindow
    );

    if (updatedTimestamps.length >= config.burstLimit) {
      setIsBlocked(true);
      setTimeout(() => setIsBlocked(false), config.cooldownTime);
      return false;
    }

    updatedTimestamps.push(now);
    setActionTimestamps(updatedTimestamps);
    setLastActionTime(now);
    return true;
  };

  useEffect(() => {
    if (isBlocked) {
      const timeout = setTimeout(() => setIsBlocked(false), config.cooldownTime);
      return () => clearTimeout(timeout);
    }
  }, [isBlocked, config.cooldownTime]);

  return { checkAction, isBlocked };
};

// Default configuration with stricter limits
export const defaultAntiSpamConfig: AntiSpamConfig = {
  // Relaxed limits to reduce false positives for fast users
  minDelay: 50, // Minimum 50ms between actions
  burstLimit: 20, // Allow up to 20 rapid actions within the burst window
  burstWindow: 10000, // 10 seconds window for burst counting
  cooldownTime: 300, // Short cooldown after hitting burst limit
};